/*
 * product-collection-feed.js
 *
 * Feed no rodapé da PDP que continua a navegação do cliente: mesma coleção de onde ele
 * veio, retomando a paginação na posição do produto que ele clicou.
 *
 * De onde vem essa informação: o grid da coleção (main-collection-product-grid) grava em
 * sessionStorage, a cada clique num card, a chave `lolia:collection-position` com a
 * posição absoluta do item em collection.products, o handle e a URL base da listagem
 * (pathname + ordenação/filtros, sem ?page). Isso já existia para retomar o scroll na
 * volta (ver collection-scroll-memory.js) e é exatamente o que este feed precisa — por
 * isso lemos o mesmo registro em vez de criar um paralelo. Como a posição é absoluta, ela
 * é imune ao infinite scroll da coleção, que acumula páginas sem mexer na URL.
 *
 * Traduzir posição -> página é O(1): uma requisição busca a fatia certa da coleção, não
 * importa se o cliente parou no produto 10 ou no 1000.
 *
 * O que o servidor renderiza é o melhor palpite dele (a coleção da rota, quando o cliente
 * chega por /collections/x/products/y, senão o fallback da seção) e atende no-JS. Quando
 * há estado gravado que discorda dele, o grid é trocado aqui. A seção fica no fim da PDP,
 * então a troca acontece muito antes de o cliente rolar até lá.
 */
// Gravado por collection-scroll-memory.js APENAS no clique num card da coleção, e por
// este arquivo no clique num card do feed. Deliberadamente não é `lolia:collection-position`
// (o estado de scroll da listagem): aquele é reescrito a cada scroll e no pagehide da
// navegação, então o produto clicado seria substituído pelo item que estava no topo do
// viewport — e a retomada cairia na coleção do jeito errado, ou nem aconteceria.
const STORAGE_KEY = 'lolia:collection-handoff';

// Mesma janela usada por collection-scroll-memory.js: passado isso, a "coleção de origem"
// virou lixo de uma sessão de navegação antiga e é melhor cair no fallback da seção.
const STATE_MAX_AGE = 30 * 60 * 1000;

// Precisa bater com o `per_page` de templates/collection.product_feed_ajax.liquid: é o
// divisor que transforma posição absoluta em número de página. O template devolve o valor
// real em data-page-size e nós nos corrigimos na primeira resposta, caso os dois divirjam.
const AJAX_PAGE_SIZE = 12;

// Mesmo lookahead do IntersectionObserver, reusado na recontagem manual.
const INFINITE_MARGIN = 400;

// Páginas seguidas sem nenhum card aproveitável (tudo indisponível ou já visto) antes de
// desistir. Sem esse teto, uma faixa longa de produtos esgotados viraria um loop de fetch.
const MAX_EMPTY_PAGES = 5;

class ProductCollectionFeed extends HTMLElement {
    connectedCallback() {
        this.grid = this.querySelector('[data-product-feed-grid]');
        this.loadingEl = this.querySelector('[data-product-feed-loading]');
        this.errorEl = this.querySelector('[data-product-feed-error]');
        this.loadMoreWrapper = this.querySelector('[data-product-feed-load-more]');
        this.loadMoreButton = this.querySelector('[data-product-feed-load-more-button]');
        this.sentinel = this.querySelector('[data-product-feed-sentinel]');

        if (!this.grid) return;

        this.excludeHandle = this.dataset.excludeHandle;
        this.scrollMode = this.dataset.scrollMode;
        this.nextPage = parseInt(this.dataset.nextPage, 10) || 2;
        this.hasNext = this.dataset.hasNext === 'true';
        this.pageSize = AJAX_PAGE_SIZE;
        this.isLoading = false;
        this.emptyPages = 0;

        this.renderedBase = this.normalizeUrl(this.dataset.collectionUrl);
        this.origin = this.dataset.useOrigin === 'true' ? this.readOriginState() : null;
        this.fetchBase = (this.origin && this.origin.base) || this.renderedBase;

        if (!this.fetchBase) {
            this.hideMoreUi();
            return;
        }

        this.collectSeenHandles();
        this.trackClicks();

        // Retomada: o que está na tela é a primeira leva da coleção errada, ou da certa
        // mas do começo. Nos dois casos o grid é descartado e refeito da página correta.
        const resumePage = this.resumePage();
        if (resumePage) {
            this.nextPage = resumePage;
            this.hasNext = true;
            this.resetGrid();
            this.showMoreUi();
        }

        if (!this.hasNext) {
            this.hideMoreUi();
            return;
        }

        if (this.loadMoreButton) {
            this.loadMoreButton.addEventListener('click', () => this.loadNextPage());
        }

        if (this.scrollMode === 'infinite') {
            this.setupObserver();
        }

        if (resumePage) this.loadNextPage();
    }

    /* ------------------------------------------------------- coleção de origem */

    // Normaliza para comparação e para servir de base de fetch: fora os parâmetros de
    // paginação/render, e ordem estável para `sort_by`/`filter.*` não gerarem falso
    // negativo na comparação com a base renderizada.
    normalizeUrl(value) {
        if (!value) return null;

        let url;
        try {
            url = new URL(value, window.location.origin);
        } catch (error) {
            return null;
        }

        ['page', 'view', 'section_id'].forEach((param) => url.searchParams.delete(param));
        url.searchParams.sort();
        return url;
    }

    readOriginState() {
        let state;
        try {
            state = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
        } catch (error) {
            // Modo privado/quota: sem estado, o feed segue com o que o servidor mandou.
            return null;
        }

        if (!state || typeof state.key !== 'string') return null;
        if (typeof state.at === 'number' && (Date.now() - state.at) > STATE_MAX_AGE) return null;

        // O registro só descreve a chegada NESTA PDP se o produto clicado é este produto.
        // Sem essa checagem, quem cai aqui pela busca ou por um carrossel de outra PDP
        // herdaria a coleção de uma navegação anterior que ainda está na janela de 30min.
        if (!state.handle || state.handle !== this.excludeHandle) return null;

        const base = this.normalizeUrl(state.key);
        // Só listagem de coleção serve como origem de paginação. O regex (e não um
        // startsWith) porque a URL pode vir com prefixo de idioma/mercado.
        if (!base || !/\/collections\/[^/]+/.test(base.pathname)) return null;

        const position = parseInt(state.position, 10);

        return {
            base: base,
            position: Number.isNaN(position) || position < 0 ? null : position,
            handle: state.handle || null
        };
    }

    // Página do feed em que a paginação deve retomar, ou 0 quando o que o servidor já
    // renderizou serve.
    resumePage() {
        if (!this.origin) return 0;

        const page = this.origin.position === null
            ? 1
            : Math.floor(this.origin.position / this.pageSize) + 1;

        // A página que contém o produto clicado, não a seguinte: assim o feed reabre no
        // trecho que o cliente estava olhando (o produto em si é excluído do grid).
        if (page === 1 && this.sameBase(this.origin.base, this.renderedBase)) return 0;

        return page;
    }

    sameBase(a, b) {
        if (!a || !b) return false;
        return a.pathname === b.pathname && a.searchParams.toString() === b.searchParams.toString();
    }

    // Clique num card do feed: grava a posição para a PDP seguinte continuar daqui, no
    // mesmo formato que o grid da coleção usa. Só o handoff é tocado — o estado de scroll
    // da listagem é de outro dono e não deve ser mexido a partir da PDP.
    trackClicks() {
        this.grid.addEventListener('click', (event) => {
            const target = event.target;
            const link = target && target.closest ? target.closest('a[href]') : null;
            if (!link) return;

            const item = link.closest('[data-product-feed-item][data-position]');
            if (item) this.writeOriginState(item);
        }, true);
    }

    writeOriginState(item) {
        const position = parseInt(item.dataset.position, 10);
        if (Number.isNaN(position)) return;

        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                key: this.fetchBase.pathname + this.fetchBase.search,
                at: Date.now(),
                position: String(position),
                handle: item.dataset.productHandle || null
            }));
        } catch (error) {
            /* modo privado / quota: a próxima PDP cai no fallback da seção */
        }
    }

    /* ------------------------------------------------------------------- grid */

    collectSeenHandles() {
        this.seenHandles = new Set(
            [...this.grid.querySelectorAll('[data-product-feed-item]')]
                .map((item) => item.dataset.productHandle)
                .filter(Boolean)
        );
    }

    // Remover o <li> não devolve memória por si: updateSlideGridProduct() inicializa um
    // slick por card, e o slick registra handlers em window que seguram a instância (e o
    // card inteiro) mesmo fora do documento. Mesmo cuidado de collection-scroll-memory.js.
    resetGrid() {
        const $ = window.jQuery;

        if ($) {
            const $grid = $(this.grid);
            try {
                $grid.find('.card-media.slick-initialized').slick('unslick');
            } catch (error) {
                /* slider já desmontado ou slick ausente: seguir com a limpeza */
            }
            $grid.children().remove();
        } else {
            this.grid.textContent = '';
        }

        this.seenHandles = new Set();
    }

    setupObserver() {
        if (!this.sentinel || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && this.hasNext && !this.isLoading) {
                    this.loadNextPage();
                }
            });
        }, { rootMargin: INFINITE_MARGIN + 'px 0px' });

        this.observer.observe(this.sentinel);
    }

    hideMoreUi() {
        if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = true;
        if (this.sentinel) this.sentinel.hidden = true;
        if (this.observer) this.observer.disconnect();
    }

    showMoreUi() {
        if (this.loadMoreWrapper) this.loadMoreWrapper.hidden = false;
        if (this.sentinel) this.sentinel.hidden = false;
    }

    setLoading(isLoading) {
        if (this.loadingEl) this.loadingEl.hidden = !isLoading;
    }

    setError(hasError) {
        if (this.errorEl) this.errorEl.hidden = !hasError;

        if (this.loadMoreButton) {
            this.loadMoreButton.disabled = false;
            this.loadMoreButton.textContent = hasError
                ? (this.loadMoreButton.dataset.errorText || 'Erro ao carregar. Tentar novamente')
                : (this.loadMoreButton.dataset.defaultText || this.loadMoreButton.textContent);
        }
    }

    /* -------------------------------------------------------------- paginação */

    pageUrl(page) {
        const url = new URL(this.fetchBase.href);
        url.searchParams.set('page', page);
        url.searchParams.set('view', 'product_feed_ajax');
        return url.toString();
    }

    async loadNextPage() {
        if (this.isLoading || !this.hasNext) return;

        this.isLoading = true;
        this.setError(false);
        this.setLoading(true);
        if (this.loadMoreButton) this.loadMoreButton.disabled = true;

        let shouldContinue = false;

        try {
            const response = await fetch(this.pageUrl(this.nextPage));

            if (!response.ok) {
                throw new Error(`Resposta inesperada do servidor (status ${response.status})`);
            }

            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const meta = parsed.querySelector('[data-product-feed-meta]');

            // Only an explicit `data-has-next` marker from the server means "end of
            // collection" — any other failure here is treated as transient, not as the
            // natural end of the feed, so the button/sentinel stays available to retry.
            if (!meta) {
                throw new Error('Marcador de paginação ausente na resposta.');
            }

            const items = [...parsed.querySelectorAll('[data-product-feed-item]')];
            const fragment = document.createDocumentFragment();
            let appended = 0;

            items.forEach((item) => {
                const handle = item.dataset.productHandle;
                if (!handle || handle === this.excludeHandle || this.seenHandles.has(handle)) return;
                this.seenHandles.add(handle);
                fragment.appendChild(item);
                appended += 1;
            });

            this.grid.appendChild(fragment);

            this.hasNext = meta.dataset.hasNext === 'true';
            this.nextPage = parseInt(meta.dataset.nextPage, 10) || (this.nextPage + 1);

            if (!this.hasNext) {
                this.hideMoreUi();
            }

            // Uma pagina inteira pode ficar vazia depois do filtro de disponibilidade.
            // O sentinel nao se move nesse caso, entao o IntersectionObserver nao dispara
            // de novo e o feed travaria: seguimos para a proxima pagina por conta propria.
            this.emptyPages = appended === 0 ? this.emptyPages + 1 : 0;
            shouldContinue = appended === 0 && this.hasNext && this.emptyPages < ProductCollectionFeed.MAX_EMPTY_PAGES;

            window.updateSlideGridProduct?.();
        } catch (error) {
            this.setError(true);
            console.error('[product-collection-feed] falha ao carregar próxima página:', error);
        } finally {
            this.isLoading = false;
            this.setLoading(false);
            if (this.loadMoreButton) this.loadMoreButton.disabled = false;
        }

        if (shouldContinue) {
            await this.loadNextPage();
        }

        // Fora do finally: `loadNextPage` precisa que isLoading já esteja liberado.
        if (this.chainPending) {
            this.chainPending = false;
            this.loadNextPage();
            return;
        }

        this.maybeContinue();
    }

    appendItems(parsed) {
        const fragment = document.createDocumentFragment();
        let appended = 0;

        parsed.querySelectorAll('[data-product-feed-item]').forEach((item) => {
            const handle = item.dataset.productHandle;
            if (!handle || handle === this.excludeHandle || this.seenHandles.has(handle)) return;

            this.seenHandles.add(handle);
            fragment.appendChild(item);
            appended += 1;
        });

        this.grid.appendChild(fragment);
        return appended;
    }

    // O IntersectionObserver só dispara em cruzamento de fronteira: se a leva que acabou
    // de entrar for curta e o sentinel nunca sair da margem, ele não avisa de novo e o
    // feed trava. Uma conferência explícita por posição cobre esse caso.
    maybeContinue() {
        if (this.scrollMode !== 'infinite' || !this.hasNext || this.isLoading) return;
        if (!this.sentinel || this.sentinel.hidden) return;

        const viewport = window.innerHeight || 0;
        if (this.sentinel.getBoundingClientRect().top <= viewport + INFINITE_MARGIN) {
            this.loadNextPage();
        }
    }
}

// Teto de paginas consecutivas sem nenhum produto disponivel, para nao varrer uma
// colecao inteira esgotada de uma vez.
ProductCollectionFeed.MAX_EMPTY_PAGES = 10;

customElements.define('product-collection-feed', ProductCollectionFeed);
